import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdFactCheck,
    MdPeople,
    MdSearch,
    MdDescription,
    MdBusinessCenter,
} from 'react-icons/md';
import './FloatingActions.css';

const FloatingActions = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 80 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const isDragGesture = useRef(false);

    const toggleOpen = () => {
        if (!isDragGesture.current) {
            setIsOpen(!isOpen);
        }
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        isDragGesture.current = false;
        dragStartPos.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
    };

    const handleTouchStart = (e) => {
        setIsDragging(true);
        isDragGesture.current = false;
        const touch = e.touches[0];
        dragStartPos.current = {
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        };
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            isDragGesture.current = true;
            e.preventDefault();

            const newX = e.clientX - dragStartPos.current.x;
            const newY = e.clientY - dragStartPos.current.y;

            // Boundary checks
            const maxX = window.innerWidth - 64; // button width
            const maxY = window.innerHeight - 64; // button height

            setPosition({
                x: Math.min(Math.max(0, newX), maxX),
                y: Math.min(Math.max(0, newY), maxY)
            });
        }
    };

    const handleTouchMove = (e) => {
        if (isDragging) {
            isDragGesture.current = true;
            const touch = e.touches[0];

            const newX = touch.clientX - dragStartPos.current.x;
            const newY = touch.clientY - dragStartPos.current.y;

            // Boundary checks
            const maxX = window.innerWidth - 64;
            const maxY = window.innerHeight - 64;

            setPosition({
                x: Math.min(Math.max(0, newX), maxX),
                y: Math.min(Math.max(0, newY), maxY)
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);

        // Snap to nearest edge
        const windowWidth = window.innerWidth;
        const buttonWidth = 64; // defined in CSS
        const threshold = windowWidth / 2;

        let newX;
        let newY = position.y; // Keep vertical position

        if (position.x + buttonWidth / 2 < threshold) {
            newX = 16; // Snap to left with margin
        } else {
            newX = windowWidth - buttonWidth - 16; // Snap to right with margin
        }

        // Ensure Y is within bounds (in case it drifted)
        const maxY = window.innerHeight - buttonWidth;
        newY = Math.min(Math.max(0, newY), maxY);

        setPosition({ x: newX, y: newY });
    };

    useEffect(() => {
        // Adjust position on resize to keep on screen
        const handleResize = () => {
            const windowWidth = window.innerWidth;
            const buttonWidth = 64;

            setPosition(prev => {
                // Keep proportional or just clamp? Clamp is safer.
                // Also re-snap if it was snapped? For now simple clamp.
                let newX = prev.x;
                if (prev.x + buttonWidth / 2 > windowWidth / 2) {
                    newX = windowWidth - buttonWidth - 16;
                } else {
                    newX = 16;
                }

                return {
                    x: newX,
                    y: Math.min(prev.y, window.innerHeight - 64)
                };
            });
        };

        window.addEventListener('resize', handleResize);

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, position]);

    const actions = [
        { label: 'Create Inward', icon: <MdAdd />, onClick: () => navigate('/inward/entry') },
        { label: 'View Quotations', icon: <MdFactCheck />, onClick: () => navigate('/view-quotations') },
        { label: 'Add Vendor', icon: <MdPeople />, onClick: () => navigate('/vendors/new') },
        { label: 'Create Project', icon: <MdBusinessCenter />, onClick: () => navigate('/projects/new') },
        { label: 'Generate Request', icon: <MdDescription />, onClick: () => navigate('/generate-requests/new') },

    ];

    // Calculate menu direction
    const verticalDir = position.y > window.innerHeight / 2 ? 'up' : 'down';
    const horizontalDir = position.x > window.innerWidth / 2 ? 'left' : 'right';

    return (
        <div
            className={`fab-container ${isOpen ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{ left: position.x, top: position.y }}
        >
            <button
                className={`fab-main-button ${isOpen ? 'open' : ''}`}
                onClick={toggleOpen}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                aria-label="Quick Actions"
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                {isOpen ? <MdAdd style={{ transform: 'rotate(45deg)' }} /> : <MdAdd />}
            </button>

            <div className={`fab-menu menu-${verticalDir} menu-${horizontalDir}`}>
                {actions.map((action, index) => (
                    <div key={index} className="fab-item">
                        <span className="fab-label">{action.label}</span>
                        <button
                            className="fab-button"
                            onClick={() => {
                                action.onClick();
                                setIsOpen(false);
                            }}
                            title={action.label}
                        >
                            {action.icon}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FloatingActions;
